import { useEffect, useMemo, useState } from 'react';
import { supabase, CRM_API_BASE } from '../lib/supabase';
import {
  RoutingRulesApi,
  EmployeesApi,
  type RoutingRule,
  type RoutingRuleInput,
  type EmployeeOption,
} from '../lib/crmApi';

interface Employee {
  id: number;
  name: string;
  whu_user_id: string | null;
  role: string;
  is_active: boolean;
  last_ping_at: string | null;
}
interface OutboxRow {
  id: number;
  source: string;
  status: string;
  attempts: number;
  created_at: string;
}

const COMMON_EVENTS = [
  'menu_agendar',
  'menu_cardio',
  'menu_retorno',
  'menu_exames',
  'menu_duvida',
  'hot_lead',
];
const FALLBACK_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '— nenhum (rejeita)' },
  { value: 'attendant', label: 'Qualquer atendente online' },
  { value: 'roleta_whu', label: 'Roleta WHU (fallback legado)' },
];

const EMPTY_FORM: RoutingRuleInput = {
  name: '',
  source_event: 'menu_agendar',
  priority: 100,
  target_employee_id: null,
  fallback_role: null,
  active: true,
};

export default function AdminSettings() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [dlq, setDlq] = useState<OutboxRow[]>([]);
  const [healthOk, setHealthOk] = useState<boolean | null>(null);
  const [loadingRules, setLoadingRules] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<RoutingRule | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<RoutingRuleInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    reload();
  }, []);

  async function reload() {
    setLoadingRules(true);
    setError(null);
    const [{ data: e }, rulesRes, empRes, { data: d }, h] = await Promise.all([
      supabase.from('employees').select('*').order('name'),
      RoutingRulesApi.list(),
      EmployeesApi.list(),
      supabase
        .from('webhook_outbox')
        .select('id, source, status, attempts, created_at')
        .eq('status', 'dlq')
        .limit(20),
      fetch(`${CRM_API_BASE}/healthz`)
        .then((r) => r.ok)
        .catch(() => false),
    ]);
    setEmployees(e ?? []);
    setRules(rulesRes.ok ? rulesRes.data?.data ?? [] : []);
    setEmployeeOptions(empRes.ok ? empRes.data?.data ?? [] : []);
    setDlq(d ?? []);
    setHealthOk(h);
    if (!rulesRes.ok) setError(`Routing rules: ${rulesRes.error ?? 'falha'}`);
    setLoadingRules(false);
  }

  const employeeName = useMemo(() => {
    const m = new Map<number, string>();
    for (const emp of employeeOptions) m.set(emp.id, emp.name);
    return m;
  }, [employeeOptions]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  }
  function openEdit(rule: RoutingRule) {
    setEditing(rule);
    setForm({
      name: rule.name,
      source_event: rule.source_event,
      priority: rule.priority,
      target_employee_id: rule.target_employee_id,
      fallback_role: rule.fallback_role,
      active: rule.active,
    });
    setFormOpen(true);
  }
  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  async function submitForm() {
    if (!form.name.trim()) {
      setError('Nome obrigatório');
      return;
    }
    if (!form.source_event.trim()) {
      setError('Source event obrigatório');
      return;
    }
    if (!form.target_employee_id && !form.fallback_role) {
      setError('Escolha um funcionário alvo OU um fallback.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = editing
        ? await RoutingRulesApi.update(editing.id, form)
        : await RoutingRulesApi.create(form);
      if (!res.ok) {
        setError(res.error ?? 'Falha ao salvar');
        return;
      }
      await reload();
      closeForm();
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(rule: RoutingRule) {
    const res = await RoutingRulesApi.update(rule.id, { active: !rule.active });
    if (!res.ok) setError(res.error ?? 'Falha ao alternar');
    else setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
  }

  async function removeRule(rule: RoutingRule) {
    if (!confirm(`Deletar regra "${rule.name}"? Não dá pra desfazer.`)) return;
    const res = await RoutingRulesApi.remove(rule.id);
    if (!res.ok) setError(res.error ?? 'Falha ao deletar');
    else setRules((prev) => prev.filter((r) => r.id !== rule.id));
  }

  const isOnline = (ts: string | null) =>
    ts && Date.now() - new Date(ts).getTime() < 2 * 60 * 1000;

  const rulesByEvent = useMemo(() => {
    const m = new Map<string, RoutingRule[]>();
    for (const r of rules) {
      if (!m.has(r.source_event)) m.set(r.source_event, []);
      m.get(r.source_event)!.push(r);
    }
    return m;
  }, [rules]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Admin / Settings</h1>
        <p className="text-sm text-slate-500">Configurações operacionais do CRM</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-800">
            ×
          </button>
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-medium mb-3">Saúde dos serviços</h2>
        <div className="text-sm">
          crm-api:{' '}
          {healthOk === null ? (
            '…'
          ) : healthOk ? (
            <span className="text-emerald-600">OK</span>
          ) : (
            <span className="text-red-600">DOWN</span>
          )}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-medium mb-3">Funcionárias ({employees.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b">
            <tr>
              <th className="py-2">Nome</th>
              <th>Role</th>
              <th>WHU userId</th>
              <th>Online</th>
              <th>Último ping</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id} className="border-b last:border-0">
                <td className="py-2">{e.name}</td>
                <td>{e.role}</td>
                <td className="text-xs font-mono text-slate-500">{e.whu_user_id ?? '—'}</td>
                <td>
                  {isOnline(e.last_ping_at) ? (
                    <span className="text-emerald-600">●</span>
                  ) : (
                    <span className="text-slate-300">●</span>
                  )}
                </td>
                <td className="text-xs text-slate-500">
                  {e.last_ping_at ? new Date(e.last_ping_at).toLocaleString('pt-BR') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-medium">Routing Rules ({rules.length})</h2>
            <p className="text-xs text-slate-500">
              Regras de roteamento de hot lead por evento. Ordem por prioridade (menor → primeiro).
            </p>
          </div>
          <button
            onClick={openCreate}
            className="bg-emerald-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-emerald-700"
          >
            + Nova regra
          </button>
        </div>

        {loadingRules ? (
          <p className="text-sm text-slate-500">Carregando…</p>
        ) : rules.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nenhuma regra cadastrada. Clique em "Nova regra" pra criar.
          </p>
        ) : (
          <div className="space-y-4">
            {[...rulesByEvent.entries()].map(([event, list]) => (
              <div key={event} className="border border-slate-100 rounded">
                <div className="bg-slate-50 px-3 py-2 text-xs font-mono text-slate-600 border-b">
                  {event}
                </div>
                <table className="w-full text-sm">
                  <thead className="text-left text-slate-500 text-xs border-b">
                    <tr>
                      <th className="py-2 pl-3">Prio</th>
                      <th>Nome</th>
                      <th>Alvo</th>
                      <th>Fallback</th>
                      <th>Ativa</th>
                      <th className="pr-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 pl-3 font-mono text-xs">{r.priority}</td>
                        <td>{r.name}</td>
                        <td>
                          {r.target_employee_id
                            ? employeeName.get(r.target_employee_id) ?? `#${r.target_employee_id}`
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="text-xs">
                          {r.fallback_role ?? <span className="text-slate-400">—</span>}
                        </td>
                        <td>
                          <button
                            onClick={() => toggleActive(r)}
                            className={`text-xs px-2 py-0.5 rounded ${
                              r.active
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                          >
                            {r.active ? 'ativa' : 'inativa'}
                          </button>
                        </td>
                        <td className="pr-3 text-right space-x-2">
                          <button
                            onClick={() => openEdit(r)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            editar
                          </button>
                          <button
                            onClick={() => removeRule(r)}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            deletar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-medium mb-3">Webhook DLQ ({dlq.length} últimos 20)</h2>
        {dlq.length === 0 ? (
          <p className="text-sm text-slate-500">Vazio — nada pra reprocessar.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b">
              <tr>
                <th className="py-2">ID</th>
                <th>Source</th>
                <th>Attempts</th>
                <th>Criado em</th>
              </tr>
            </thead>
            <tbody>
              {dlq.map((o) => (
                <tr key={o.id} className="border-b last:border-0">
                  <td className="py-2 font-mono text-xs">{o.id}</td>
                  <td>{o.source}</td>
                  <td>{o.attempts}</td>
                  <td className="text-xs">{new Date(o.created_at).toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {formOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={closeForm}
        >
          <div
            className="bg-white rounded-lg w-full max-w-lg p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">
              {editing ? 'Editar regra' : 'Nova regra de routing'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 block mb-1">Nome *</label>
                <input
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Caroline coordena"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Source event *</label>
                  <input
                    list="common-events"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
                    value={form.source_event}
                    onChange={(e) => setForm({ ...form, source_event: e.target.value })}
                    placeholder="menu_agendar"
                  />
                  <datalist id="common-events">
                    {COMMON_EVENTS.map((ev) => (
                      <option key={ev} value={ev} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Prioridade</label>
                  <input
                    type="number"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    value={form.priority}
                    min={0}
                    max={100000}
                    onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-600 block mb-1">Funcionário alvo</label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={form.target_employee_id ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      target_employee_id: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                >
                  <option value="">— nenhum (usa fallback)</option>
                  {employeeOptions.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} ({emp.role}){emp.is_active ? '' : ' • inativa'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-slate-600 block mb-1">Fallback</label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={form.fallback_role ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, fallback_role: e.target.value || null })
                  }
                >
                  {FALLBACK_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Usado quando o alvo está offline ou não setado.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                <label htmlFor="active" className="text-sm">
                  Regra ativa
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={closeForm}
                disabled={saving}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
              >
                Cancelar
              </button>
              <button
                onClick={submitForm}
                disabled={saving}
                className="bg-emerald-600 text-white px-4 py-1.5 rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Salvando…' : editing ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
